/**
 * Analytics Configuration
 * Privacy-compliant analytics setup for the Nexus application
 */

import { ConsoleAnalyticsProvider, type AnalyticsConfig } from '@repo/core'

/**
 * Create analytics configuration based on environment
 */
export function createAnalyticsConfig(): AnalyticsConfig {
  const isDevelopment = process.env['NODE_ENV'] === 'development'
  const isProduction = process.env['NODE_ENV'] === 'production'

  return {
    // Enable analytics in all environments (can be disabled via env var)
    enabled: import.meta.env.VITE_ANALYTICS_ENABLED !== 'false',

    // Debug mode in development
    debug: isDevelopment,

    // Privacy settings
    respectDNT: true, // Respect Do Not Track browser setting
    autoPageViews: true, // Track page views automatically
    autoErrorTracking: true, // Track errors automatically
    performanceMonitoring: true, // Track performance metrics

    // Providers
    providers: [
      // Always use console provider in development for debugging
      new ConsoleAnalyticsProvider(),

      // Add PostHog in production if API key is provided
      ...(isProduction && import.meta.env.VITE_POSTHOG_KEY
        ? [
            // Dynamically import PostHog provider
            // NOTE: Uncomment when ready to use PostHog
            // new PostHogAnalyticsProvider()
          ]
        : []),
    ],

    // Privacy & Compliance
    privacy: {
      // Anonymize IP addresses
      anonymizeIP: true,

      // Require user consent before tracking (GDPR/CCPA compliance)
      requireConsent: true,

      // Cookie consent duration (days)
      cookieConsentDuration: 365,

      // Allow users to opt-out
      allowOptOut: true,
    },

    // Sampling (sample 100% of events by default)
    sampleRate: parseFloat(import.meta.env.VITE_ANALYTICS_SAMPLE_RATE || '1.0'),
  }
}
